<template>
	<v-container fluid>
		<v-row>
			<v-col cols="12" class="px-0">
				<v-card class="my-report-card my-report-card--with-overlay">
					<div
						v-if="showTableLoadingOverlay"
						class="my-report-table-loading-overlay">
						<v-progress-circular
							indeterminate
							color="primary"
							size="40"
						/>
					</div>
					<InfiniteScroll
						id="ReportTable"
						item-key="id"
						fit-available-viewport
						:headers="headers"
						:items="reports"
						:loading="loadingAppend"
						:has-more="hasMoreReports"
						:auto-load-on-mount="false"
						enable-column-resize
						fit-resizable-columns-to-wrapper
						:resizable-columns="resizableColumns"
						hide-default-footer
						disable-pagination
						class="my-report-table"
						@load-more="loadMoreReports">
						<template v-slot:no-data>
							<span>{{ $t('reports.table.noData') }}</span>
						</template>
						<template v-slot:[`item.reportLayout`]="{ }">
							<OneLineCell :text="reportLayoutLabel" />
						</template>
						<template v-slot:[`item.reportName`]="{ item }">
							<OneLineCell :text="item.reportName || '-'" />
						</template>
						<template v-slot:[`item.updatedAt`]="{ item }">
							<OneLineCell
								:text="item.updatedAt
									? formatter.formatDateTimeZoneWithHours(item.updatedAt)
									: '-'"
							/>
						</template>
						<template v-slot:[`item.createdAt`]="{ item }">
							<OneLineCell
								:text="item.createdAt
									? formatter.formatDateTimeZoneWithHours(item.createdAt)
									: '-'"
							/>
						</template>
						<template v-slot:[`item.financialGroupId`]="{ item }">
							<OneLineCell
								:text="item.financialGroupId
									? (getFinancialGroup(item.financialGroupId).name)
									: '-'"
							/>
						</template>
						<template v-slot:[`item.requestUserName`]="{ item }">
							<OneLineCell
								:text="item.requestUserName
									? formatter.formatToTitleCase(item.requestUserName)
									: '-'"
							/>
						</template>
						<template v-slot:[`item.status`]="{ item }">
							<OneLineCell
								:text="getReportsStatus(item.status)" />
						</template>
						<template v-slot:[`item.errors`]="{ item }">
							<v-tooltip bottom>
								<template v-slot:activator="{ on, attrs }">
									<div v-bind="attrs" v-on="on">
										<v-btn
											color="error"
											icon
											@click="openMyReportsErrorModal(item.id)"
											:disabled="!item.hasError">
											<v-icon
												:color="item.hasError ? 'error' : ''"
												size="25">
												fas fa-times
											</v-icon>
										</v-btn>
									</div>
								</template>
								<span> {{ $t('reports.table.tooltipError') }} </span>
							</v-tooltip>
						</template>
						<template v-slot:[`item.actions`]="{ item }">
							<v-tooltip bottom>
								<template v-slot:activator="{ on, attrs }">
									<div v-bind="attrs" v-on="on">
										<v-btn
											:disabled="item.status != 'SUCCESS'"
											icon
											:loading="loadingReports[item.id]"
											@click="downloadReport(item.id, item.reportName)">
											<v-icon color="base-color">
												mdi-tray-arrow-down
											</v-icon>
										</v-btn>
									</div>
								</template>
								<span>
									Baixar relatório
								</span>
							</v-tooltip>
						</template>
					</InfiniteScroll>
				</v-card>
			</v-col>
		</v-row>

		<SnackbarCustomize ref="SnackbarCustomize" />

		<MyReportsErrorModal
			ref="MyReportsErrorModal"
			customDataTableId="ReportsErrorTable"
			:myReportsError="propsError"
			:loadingTableMyReportsError="loadingErrorsModal"
			@cleanErrors="propsError = []"
		/>
	</v-container>
</template>

<script>
	import MyReportsErrorModal from '@/components/DashboardMovementRecords/MyReports/MyReportsErrorModal.vue';
	import AuthorityManagementMixin from "@/shared/mixins/authorityManagement/authorityManagementMixin";
	import ReportAutomationMixin from '@/shared/mixins/reportAutomation/reportAutomationMixin';
	import MovementRecordsMixin from "@/shared/mixins/movementRecords/movementRecordsMixin";
	import SnackbarCustomize from "@/components/CustomAlerts/SnackbarCustomize.vue";
	import MyReportsService from "@/services-http/sdi/MyReportsService.js";
	import MyReportsMixin from "@/shared/mixins/myReports/myReportsMixin";
	import InfiniteScroll from '@/components/Default/InfiniteScroll.vue';
	import FileDataService from '@/services-http/sdi/FileDataService';
	import ReportMixin from '@/shared/mixins/report/reportMixin';
	import Formatters from "@/shared/formatters/formatters";
	import OneLineCell from '../Default/OneLineCell.vue';
	import { cloneDeep } from "lodash";
	import { mapGetters } from 'vuex';

const MY_REPORT_COLUMN_CONFIG = {
	reportLayout: { width: 150, className: 'col-report-layout' },
	reportName: { width: 280, className: 'col-report-name' },
	financialGroupId: { width: 150, className: 'col-report-financial-group' },
	requestUserName: { width: 160, className: 'col-report-request-user' },
	createdAt: { width: 170, className: 'col-report-created-at' },
	updatedAt: { width: 170, className: 'col-report-updated-at' },
	status: { width: 150, className: 'col-report-status' },
	errors: { width: 80, className: 'col-report-errors' },
	actions: { width: 80, className: 'col-report-actions' },
};

const MY_REPORT_HEADER_DEFINITIONS = [
	{ textKey: 'reports.table.headers.reportLayout', value: 'reportLayout' },
	{ textKey: 'reports.table.headers.reportName', value: 'reportName' },
	{ textKey: 'reports.table.headers.financialGroup', value: 'financialGroupId' },
	{ textKey: 'reports.table.headers.requestedBy', value: 'requestUserName' },
	{ textKey: 'reports.table.headers.requestDate', value: 'createdAt' },
	{ textKey: 'reports.table.headers.issueDate', value: 'updatedAt' },
	{ textKey: 'reports.table.headers.status', value: 'status' },
	{ textKey: 'reports.table.headers.error', value: 'errors' },
	{ textKey: 'reports.table.headers.actions', value: 'actions' },
];

export default {
	name: "ReportTable",
	components: {
		SnackbarCustomize,
		MyReportsErrorModal,
    InfiniteScroll,
		OneLineCell,
	},
	data: () => ({
		loadingReports: {},
		loading: false,
		loadingAppend: false,
		hasAuthorityUploadMyReports: false,
		loadingErrorsModal: false,
		reportServices: null,
		fileDataService: null,
		page: 1,
		itemsPerPage: 25,
		totalPages: 0,
		propsError: [],
		reports: [],
	}),
	mixins: [
		AuthorityManagementMixin,
		ReportAutomationMixin,
		MovementRecordsMixin,
		MyReportsMixin,
		ReportMixin,
	],
	props: {
		filter: {
			type: Object,
		},
		reportDefaultType: {
			type: String,
			default: 'REPORT_INSURED'
		},
		reportLayoutName: {
      type: String,
			default: null,
    },
		isAdmissionMapReport: {
			type: Boolean,
			default: false,
		},
		fetchReports: {
			type: Function,
			default: null,
		},
	},
	watch: {
		filter: {
			handler(newFilter) {
				this.filterMyReports = newFilter;
			}
		}
	},
	created() {
		this.formatter = new Formatters()
	},
	async mounted() {
		this.hasAuthorityUploadMyReports = true
		this.fileDataService = new FileDataService();
		this.reportServices = new MyReportsService();

		await this.getReports(true)
	},
	methods: {
		// Update filters and restart report search
		async handlerFilter(value, type) {
			this.filterMyReports = cloneDeep(value)
			await this.getReports(true)
		},
		async loadMoreReports() {
			if (this.loading || !this.hasMoreReports) return;
			await this.getReports();
		},
		// Download the selected report
		async downloadReport(id, filename) {
			this.$set(this.loadingReports, id, true);
			await this.fileDataService.DownloadReport(id).then((response) => {
        if (response) {
          const fileURL = window.URL.createObjectURL(new Blob([response.data]));
          const fileLink = document.createElement('a');
          fileLink.href = fileURL;
          fileLink.setAttribute('download', `${filename}`);
          document.body.appendChild(fileLink);
          fileLink.click();
					this.$set(this.loadingReports, id, false);
          this.$refs.SnackbarCustomize.open('success', this.$t('reports.messages.downloadSuccess'), 3000);
        }
      }).catch(() => {
				this.$set(this.loadingReports, id, false);
        this.$refs.SnackbarCustomize.open('error', this.$t('reports.messages.downloadError'));
      });
		},
		// Search reports with current filters
		async getReports(reset = false) {
			if (this.loading && !reset) return;
			if (reset) {
				this.page = 1;
				this.reports = [];
				this.totalPages = 0;
				this.loadingAppend = false;
			} else {
				this.loadingAppend = true;
			}
			this.loading = true;
			try {
				const filteredParams = this.getFilteredParams();
				if ('status' in filteredParams && filteredParams.status.length > 0 && filteredParams.status.includes("PARTIAL_FINISH")) {
					filteredParams.isPartialFinished = true;
				}
				const params = this.buildSearchParams(filteredParams, "search");
				const response = typeof this.fetchReports === 'function'
					? await this.fetchReports(params)
					: await this.reportServices.GetReports(params);
				const data = response?.data ?? response ?? {};
				const content = Array.isArray(data)
					? data
					: (Array.isArray(data.content) ? data.content : []);
				const customFetch = typeof this.fetchReports === 'function';
				this.totalPages = customFetch
					? content.length
					: (data.totalElements || 0);
				this.reports = customFetch || reset || this.page === 1
					? content
					: [...this.reports, ...content];
				if (!customFetch && this.reports.length < this.totalPages) {
					this.page += 1;
				}
			} catch (error) {
				this.$refs.SnackbarCustomize.open('error', this.$t('reports.messages.fetchError'));
			}
			this.loading = false;
			this.loadingAppend = false;
		},
		// Assembles and returns the parameters of the this.filterMyReports object
		getFilteredParams() {
			return Object.entries(this.filterMyReports)
				.filter(([key, value]) => value && value !== "")
				.reduce((element, [key, value]) => ({ ...element, [key]: value }), {})
		},
		// Assembles and returns the parameters that will be sent in the request
		buildSearchParams(filteredParams, type) {
			const formattedFilterParams = cloneDeep(filteredParams)
			this.deleteEmptyParams(formattedFilterParams)
			return new URLSearchParams({
				...formattedFilterParams,
				reportLayout: this.reportDefaultType,
				isRHProtegido: this.isRHProtegido,
				page: this.page - 1,
				size: this.itemsPerPage,
				sort: "id,desc"
			})
		},
		// Remove parameters with value equal to '', null or undefined
		deleteEmptyParams(params) {
			Object.keys(params).forEach((key) => {
				if (["", null, undefined].some(item => item === params[key])) delete params[key]
			})
		},
		// Presents screen errors through the dialog component
		async openMyReportsErrorModal(reportId) {
			await this.reportServices.GetReportError(reportId).then((res) => {
				if (res && res.data && res.data.length > 0) {
					this.propsError = res.data;
				}
			}).catch(() => {
				this.$refs.SnackbarCustomize.open('error', this.$t('reports.messages.fetchError'));
			});
      this.$refs.MyReportsErrorModal.openModal();
		},
		// searches the financial group presented in the data-table
		getFinancialGroup(financialGroupId) {
			return this.financialGroups.find(el => el.id === financialGroupId) || '-'
		},
		buildHeader({ textKey, value }) {
			const columnConfig = MY_REPORT_COLUMN_CONFIG[value] || { width: 120, className: '' };
			const width = value === 'reportName' && this.isAdmissionMapReport
				? 450
				: columnConfig.width;
			const className = columnConfig.className;

			return {
				text: this.$t(textKey),
				value,
				align: 'start',
				sortable: false,
				width,
				class: className,
				cellClass: className,
			};
		},
	},
	computed: {
		...mapGetters({
			financialGroups: 'financial-group/groups',
			isRHProtegido: 'user-info/isRHProtegido',
		}),
		headers() {
			const hiddenColumns = this.isAdmissionMapReport
				? ['reportLayout', 'financialGroupId']
				: [];

			return MY_REPORT_HEADER_DEFINITIONS
				.filter(({ value }) => !hiddenColumns.includes(value))
				.map((definition) => this.buildHeader(definition));
		},
		resizableColumns() {
			return this.headers.map(({ value }) => value);
		},
		reportLayoutLabel() {
			return this.reportLayoutName || this.$t('reports.defaults.reportLayoutName');
		},
		hasMoreReports() {
			if (!this.totalPages) return false;
			return this.reports.length < this.totalPages;
		},
		showTableLoadingOverlay() {
			return this.loading && !this.loadingAppend;
		},
	}
}
</script>

<style lang="scss">
@import "@/shared/styles/myReports/table.scss";
</style>
